import Head from 'expo-router/head';
import { ScrollView } from 'react-native';
import { Text, YStack } from 'tamagui';

import PSBottomBar from '@/components/ui/PSBottomBar';
import PSButton from '@/components/ui/PSButton';
import PSText from '@/components/ui/PSText';

/**
 * ── /components/ps-bottom-bar ────────────────────────────────────────────
 *
 * PSBottomBar — the event page's framed, safe-area-inset footer (real call
 * sites: `TicketedBookingBar`/`ExternalBookingBar` in
 * `app/events/[id]/index.tsx`, the paid/free split in
 * `app/events/[id]/confirm.tsx`, and the pass-purchase screen's bar in
 * `app/memberships/purchase/confirm.tsx`). It's render-only and content-agnostic
 * (`children` only) — the one real behavior it owns is the bottom
 * safe-area inset, and the one thing it does NOT own is where it sits.
 *
 * STRUCTURAL NOTE (read before touching this file): unlike every other
 * preview in this gallery, the live `PSBottomBar` below is NOT inside the
 * `<ScrollView>`. It's a flex SIBLING placed after it, exactly like a real
 * screen (`ScrollView`/`PageScrollView` flex={1} + `PSBottomBar` flex
 * sibling, both inside one flex column — see `docs/building-components.md`'s
 * placement section). A `PSBottomBar` rendered inside scrolling content would
 * just look like a static box and prove nothing about the one behavior this
 * page exists to demonstrate: that it stays pinned while the content above
 * it scrolls away. So this file wraps both in an outer `flex={1}` `YStack`
 * (standing in for the real screen's own `flex={1}` root) instead of putting
 * everything inside the ScrollView the way `ps-button.tsx`/`date-selector.tsx`
 * do.
 * ─────────────────────────────────────────────────────────────────────────
 */

export default function PSBottomBarPreview() {
  return (
    <>
      <Head>
        <title>PSBottomBar · Putting Scene</title>
      </Head>
      <YStack flex={1} bg="$color.background">
        <ScrollView style={{ flex: 1 }}>
          <YStack items="flex-start" gap="$lg" p="$lg">
            {/* What to look for: scroll this content and confirm the bar
              pinned below never moves or scrolls away with it — that's
              flexbox reserving its space, not `position: absolute` (the
              component's docstring explains why absolute was deliberately
              ruled out: it would let scroll content run BEHIND the bar and
              bring back the manual paddingBottom bookkeeping the flex-sibling
              layout removes). */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                scroll this — the bar below should stay pinned at the bottom of
                the screen the whole time, never scroll away with this content
                (it&apos;s a flex sibling AFTER this ScrollView, not nested
                inside it)
              </Text>
            </YStack>

            {/* Frame: bg + hairline top border + standard px/pt, all locked by
              tokens — not what earns this a component (see the guide's "don't
              wrap locked style props"). What earns it is the inset below. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                frame — opaque surface, hairline top border, `$lg` padding;
                locked by tokens, not itself the reason this is a component
              </Text>
            </YStack>

            {/* Content-agnostic layout: children lay out space-between,
              hardwired (not a prop — no call site needs a different
              distribution yet). A single child — ExternalBookingBar and a
              free/RSVP confirm — has to wrap itself in a flex={1}
              justify="flex-end" (or similar) row itself, or it sits stranded
              on the left with nothing on the right to push against. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                children lay out `space-between` (price-left / button-right) — a
                lone child (ExternalBookingBar, a free/RSVP confirm) has to push
                itself right, or it sits stranded on the left
              </Text>
            </YStack>

            {/* Safe-area inset — the actual behavior that earns this a
              component. Can't be demoed statically (it reads the real
              device's home-indicator inset at render time via
              useSafeAreaInsets()), so this is a caption, not a fixture: on a
              notched/home-indicator device the bar's bottom padding below IS
              that raw inset (content rests flush with the safe-area line); on
              a device with none, it falls back to the $lg base instead of
              sitting flush against the edge. No Platform.OS fork either way. */}
            <YStack gap="$xs">
              <Text fontFamily="$body" fontSize="$small" color="$textDarkMuted">
                bottom padding on the live bar below is THIS device&apos;s raw
                `useSafeAreaInsets().bottom` (home-indicator devices), or the
                `$lg` base when there&apos;s no inset to clear — watch it change
                if you check this on a notched vs a non-notched simulator
              </Text>
            </YStack>
          </YStack>
        </ScrollView>

        {/* Live — the ONE bar a real screen ever renders at a time, as a flex
          sibling after the ScrollView above (see the docstring's structural
          note). Content mirrors the event page's TicketedBookingBar. */}
        <PSBottomBar>
          <YStack>
            <PSText.H1>₹1,499</PSText.H1>
            <PSText.Body3>2 Tickets</PSText.Body3>
          </YStack>
          <PSButton title="Book Now" onPress={() => {}} />
        </PSBottomBar>
      </YStack>
    </>
  );
}
